import { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Plus, Play, Trash2, Terminal, Pencil, Save,
  Server, Box, ChevronDown, ChevronRight, X, Loader2,
  Zap, Monitor, HardDrive, Download, RefreshCw, Cpu,
  Network, FileCode, Square, Star, Info, ArrowRight
} from 'lucide-react';
import { commandTemplatesApi, adminApi } from '../../services/api';

interface CommandTemplate {
  _id: string;
  name: string;
  description: string;
  category: 'system' | 'docker' | 'network' | 'monitoring' | 'maintenance' | 'custom';
  command: string;
  parameters: Array<{
    name: string;
    type: 'string' | 'number' | 'boolean' | 'select';
    label: string;
    description?: string;
    required: boolean;
    defaultValue?: string;
    options?: string[];
    validation?: string;
  }>;
  requiresConfirmation: boolean;
  dangerLevel: 'safe' | 'warning' | 'danger';
  targetType: 'agent' | 'container' | 'server';
  isBuiltIn: boolean;
  isActive: boolean;
  requiredRole: 'user' | 'admin';
  usageCount: number;
  lastUsed?: string;
  isQuickAction?: boolean;
  quickActionColor?: string;
  quickActionIcon?: string;
}

interface HostAgent {
  _id: string;
  name: string;
  ipAddress: string;
  port?: number;
  isActive: boolean;
  isConnected: boolean;
}

interface OutputLine {
  type: 'stdout' | 'stderr' | 'status' | 'command' | 'error' | 'info';
  text: string;
  timestamp: Date;
}

// ── Quick Action definitions ──
const quickActions = [
  {
    id: 'system-info',
    name: 'System Info',
    icon: Monitor,
    description: 'CPU, RAM, disk, and OS details',
    command: 'echo "=== OS ===" && uname -a && echo "" && echo "=== CPU ===" && lscpu | head -15 && echo "" && echo "=== Memory ===" && free -h && echo "" && echo "=== Disk ===" && df -h / && echo "" && echo "=== Uptime ===" && uptime',
    color: 'text-blue-400',
    bg: 'bg-blue-500/10 border-blue-500/20 hover:bg-blue-500/20',
  },
  {
    id: 'docker-status',
    name: 'Docker Status',
    icon: Box,
    description: 'Docker daemon info and running containers',
    command: 'echo "=== Docker Info ===" && docker info 2>/dev/null | head -20 && echo "" && echo "=== Running Containers ===" && docker ps --format "table {{.Names}}\\t{{.Image}}\\t{{.Status}}\\t{{.Ports}}"',
    color: 'text-cyan-400',
    bg: 'bg-cyan-500/10 border-cyan-500/20 hover:bg-cyan-500/20',
  },
  {
    id: 'docker-resources',
    name: 'Container Resources',
    icon: Cpu,
    description: 'Live CPU/RAM usage of all containers',
    command: 'docker stats --no-stream --format "table {{.Name}}\\t{{.CPUPerc}}\\t{{.MemUsage}}\\t{{.NetIO}}\\t{{.PIDs}}"',
    color: 'text-amber-400',
    bg: 'bg-amber-500/10 border-amber-500/20 hover:bg-amber-500/20',
  },
  {
    id: 'disk-usage',
    name: 'Disk Usage',
    icon: HardDrive,
    description: 'Docker disk usage and volume sizes',
    command: 'echo "=== Docker Disk Usage ===" && docker system df && echo "" && echo "=== Volume Sizes ===" && du -sh /var/lib/docker/volumes/*/ 2>/dev/null | sort -rh | head -10',
    color: 'text-purple-400',
    bg: 'bg-purple-500/10 border-purple-500/20 hover:bg-purple-500/20',
  },
  {
    id: 'network-check',
    name: 'Network Check',
    icon: Network,
    description: 'Network interfaces, open ports, connectivity',
    command: 'echo "=== Interfaces ===" && ip -brief addr show && echo "" && echo "=== Listening Ports ===" && ss -tlnp | head -20 && echo "" && echo "=== Connectivity ===" && ping -c 2 8.8.8.8 2>&1 | tail -3',
    color: 'text-green-400',
    bg: 'bg-green-500/10 border-green-500/20 hover:bg-green-500/20',
  },
  {
    id: 'install-docker',
    name: 'Install Docker',
    icon: Download,
    description: 'Install Docker Engine on Ubuntu/Debian',
    command: 'curl -fsSL https://get.docker.com | sh && systemctl enable docker && systemctl start docker && docker --version',
    color: 'text-red-400',
    bg: 'bg-red-500/10 border-red-500/20 hover:bg-red-500/20',
    dangerous: true,
  },
  {
    id: 'init-agent',
    name: 'Initialize Host Agent',
    icon: Zap,
    description: 'Setup directories, firewall rules, and dependencies on the configured Agent port',
    command: 'mkdir -p /var/lib/hostinator/servers && apt-get update -qq && apt-get install -y -qq curl wget htop net-tools && echo "=== Firewall ===" && ufw allow {{agentPort}}/tcp 2>/dev/null; ufw allow 25000:35000/tcp 2>/dev/null; ufw allow 25000:35000/udp 2>/dev/null; echo "Host agent initialized successfully"',
    color: 'text-neon-400',
    bg: 'bg-neon-500/10 border-neon-500/20 hover:bg-neon-500/20',
    dangerous: true,
  },
  {
    id: 'docker-cleanup',
    name: 'Docker Cleanup',
    icon: RefreshCw,
    description: 'Prune unused images, containers, and volumes',
    command: 'echo "=== Before ===" && docker system df && echo "" && docker system prune -f && docker image prune -f && echo "" && echo "=== After ===" && docker system df',
    color: 'text-orange-400',
    bg: 'bg-orange-500/10 border-orange-500/20 hover:bg-orange-500/20',
    dangerous: true,
  },
];

export function CommandTemplatesManager() {
  const [activeSection, setActiveSection] = useState<'quick' | 'templates' | 'console'>('quick');
  const [selectedAgent, setSelectedAgent] = useState<string>('');

  const { data: agentsData } = useQuery({
    queryKey: ['host-agents-list'],
    queryFn: () => adminApi.getHostAgents({ pageSize: 100 }),
  });
  const agents: HostAgent[] = agentsData?.hostAgents || [];
  const activeAgents = agents.filter(a => a.isActive);
  const selectedAgentPort = activeAgents.find(agent => agent._id === selectedAgent)?.port ?? 3001;

  const sections = [
    { id: 'quick' as const, name: 'Quick Actions', icon: Zap },
    { id: 'templates' as const, name: 'Templates', icon: FileCode },
    { id: 'console' as const, name: 'Live Console', icon: Terminal },
  ];

  return (
    <div className="space-y-6">
      {/* Header with agent selector */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <h2 className="section-title flex-shrink-0">Command Center</h2>
        <div className="flex-1" />
        <div className="flex items-center gap-3">
          <label className="text-sm text-gray-400">Target VPC:</label>
          <select
            className="input w-64"
            value={selectedAgent}
            onChange={(e) => setSelectedAgent(e.target.value)}
          >
            <option value="">Select a VPC...</option>
            {activeAgents.map((agent) => (
              <option key={agent._id} value={agent._id}>
                {agent.name} ({agent.ipAddress})
              </option>
            ))}
          </select>
          {selectedAgent && (
            <span className="status-dot-online" title="Agent active" />
          )}
        </div>
      </div>

      {/* Section tabs */}
      <div className="flex gap-2 flex-wrap">
        {sections.map((s) => (
          <button
            key={s.id}
            onClick={() => setActiveSection(s.id)}
            className={activeSection === s.id ? 'tab-active' : 'tab'}
          >
            <s.icon className="w-4 h-4" />
            {s.name}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeSection === 'quick' && (
        <QuickActionsPanel agentId={selectedAgent} agentPort={selectedAgentPort} />
      )}
      {activeSection === 'templates' && (
        <TemplatesPanel agentId={selectedAgent} agents={activeAgents} />
      )}
      {activeSection === 'console' && (
        <LiveConsolePanel agentId={selectedAgent} agents={activeAgents} />
      )}
    </div>
  );
}

// ────────────────────────────────────────────
// Quick Actions Panel
// ────────────────────────────────────────────

function QuickActionsPanel({ agentId, agentPort }: { agentId: string; agentPort: number }) {
  const [output, setOutput] = useState<OutputLine[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [activeAction, setActiveAction] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const outputRef = useRef<HTMLDivElement>(null);

  // Fetch templates marked as Quick Actions from DB
  const { data: qaTemplates } = useQuery({
    queryKey: ['command-templates-quick-actions'],
    queryFn: () => commandTemplatesApi.getTemplates(),
  });

  const dbQuickActions = (qaTemplates?.templates || [])
    .filter((t: CommandTemplate) => t.isQuickAction)
    .map((t: CommandTemplate) => ({
      id: `tpl-${t._id}`,
      name: t.name,
      icon: getQuickActionIcon(t.quickActionIcon),
      description: t.description,
      command: t.command,
      color: `text-${t.quickActionColor || 'blue'}-400`,
      bg: `bg-${t.quickActionColor || 'blue'}-500/10 border-${t.quickActionColor || 'blue'}-500/20 hover:bg-${t.quickActionColor || 'blue'}-500/20`,
      dangerous: t.dangerLevel === 'danger',
    }));

  const allActions = [...quickActions, ...dbQuickActions];

  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [output]);

  const executeAction = useCallback((action: typeof quickActions[0]) => {
    if (!agentId) return;
    if (action.dangerous && confirming !== action.id) {
      setConfirming(action.id);
      return;
    }
    setConfirming(null);
    setIsRunning(true);
    setActiveAction(action.id);
    setOutput([{ type: 'info', text: `▶ Executing: ${action.name}`, timestamp: new Date() }]);

    const command = action.command.replace(/\{\{agentPort\}\}/g, String(agentPort));
    const controller = commandTemplatesApi.executeStream(agentId, command, {
      onStatus: (data) => {
        setOutput(prev => [...prev, { type: 'status', text: `⟳ ${data.message}`, timestamp: new Date() }]);
      },
      onCommand: (data: any) => {
        const prefix = data.total > 1 ? `[${data.index}/${data.total}] $ ` : '$ ';
        setOutput(prev => [...prev, { type: 'command', text: `${prefix}${data.command}`, timestamp: new Date() }]);
      },
      onStdout: (data) => {
        setOutput(prev => [...prev, { type: 'stdout', text: data.line, timestamp: new Date() }]);
      },
      onStderr: (data) => {
        setOutput(prev => [...prev, { type: 'stderr', text: data.line, timestamp: new Date() }]);
      },
      onDone: (data) => {
        setOutput(prev => [...prev, {
          type: 'info',
          text: `✓ Done (exit code: ${data.code ?? 'N/A'})`,
          timestamp: new Date(),
        }]);
        setIsRunning(false);
        setActiveAction(null);
      },
      onError: (data) => {
        setOutput(prev => [...prev, { type: 'error', text: `✗ ${data.message}`, timestamp: new Date() }]);
        setIsRunning(false);
        setActiveAction(null);
      },
    });
    abortRef.current = controller;
  }, [agentId, agentPort, confirming]);

  const stopExecution = () => {
    abortRef.current?.abort();
    setIsRunning(false);
    setActiveAction(null);
    setOutput(prev => [...prev, { type: 'error', text: '⏹ Aborted by user', timestamp: new Date() }]);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Actions Grid */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {allActions.map((action) => (
            <button
              key={action.id}
              onClick={() => executeAction(action)}
              disabled={!agentId || (isRunning && activeAction !== action.id)}
              className={`relative flex items-start gap-3 p-4 rounded-xl border transition-all text-left
                ${action.bg}
                ${!agentId ? 'opacity-40 cursor-not-allowed' : ''}
                ${isRunning && activeAction !== action.id ? 'opacity-40' : ''}
                ${activeAction === action.id ? 'ring-2 ring-neon-400/50' : ''}
              `}
            >
              <action.icon className={`w-6 h-6 flex-shrink-0 mt-0.5 ${action.color}`} />
              <div className="min-w-0">
                <div className="font-medium text-gray-200 text-sm flex items-center gap-2">
                  {action.name}
                  {activeAction === action.id && (
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-neon-400" />
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{action.description}</p>
              </div>
              {confirming === action.id && (
                <div className="absolute inset-0 bg-red-900/90 rounded-xl flex items-center justify-center gap-3 backdrop-blur-sm">
                  <span className="text-sm text-red-200">Confirm?</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); executeAction(action); }}
                    className="px-3 py-1 bg-red-600 text-white text-xs rounded-lg font-medium hover:bg-red-500"
                  >
                    Yes
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setConfirming(null); }}
                    className="px-3 py-1 bg-gray-700 text-gray-200 text-xs rounded-lg font-medium hover:bg-gray-600"
                  >
                    No
                  </button>
                </div>
              )}
            </button>
          ))}
        </div>

        {!agentId && (
          <div className="text-center text-gray-500 text-sm py-4 border border-dashed border-dark-600 rounded-lg">
            Select a VPC above to run commands
          </div>
        )}
      </div>

      {/* Output Terminal */}
      <div className="flex flex-col min-h-[400px]">
        <div className="flex items-center justify-between px-4 py-2 bg-dark-800 rounded-t-xl border border-dark-700 border-b-0">
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <Terminal className="w-4 h-4" />
            Output
            {isRunning && <Loader2 className="w-3.5 h-3.5 animate-spin text-neon-400" />}
          </div>
          <div className="flex items-center gap-2">
            {isRunning && (
              <button onClick={stopExecution} className="btn-ghost p-1.5 text-red-400 hover:text-red-300" title="Stop">
                <Square className="w-3.5 h-3.5" />
              </button>
            )}
            <button
              onClick={() => setOutput([])}
              className="btn-ghost p-1.5 text-gray-500 hover:text-gray-300"
              title="Clear"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        <div
          ref={outputRef}
          className="flex-1 bg-dark-900 rounded-b-xl border border-dark-700 p-3 overflow-auto font-mono text-xs leading-relaxed min-h-[350px] max-h-[600px]"
        >
          {output.length === 0 ? (
            <div className="text-gray-600 text-center py-12">
              Run a quick action to see output here
            </div>
          ) : (
            output.map((line, i) => (
              <div key={i} className={getLineClass(line.type)}>
                {line.text}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────
// Templates Panel (enhanced)
// ────────────────────────────────────────────

function TemplatesPanel({ agentId, agents }: { agentId: string; agents: HostAgent[] }) {
  const queryClient = useQueryClient();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [expandedTemplate, setExpandedTemplate] = useState<string | null>(null);
  const [executeModal, setExecuteModal] = useState<{ template: CommandTemplate } | null>(null);
  const [editModal, setEditModal] = useState<{ template: CommandTemplate } | null>(null);
  const [createModal, setCreateModal] = useState(false);

  const { data: templatesData, isLoading } = useQuery({
    queryKey: ['command-templates', selectedCategory],
    queryFn: () => commandTemplatesApi.getTemplates(
      selectedCategory !== 'all' ? { category: selectedCategory } : undefined
    ),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => commandTemplatesApi.deleteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['command-templates'] });
      queryClient.invalidateQueries({ queryKey: ['command-templates-quick-actions'] });
    },
  });

  const toggleQuickAction = useMutation({
    mutationFn: (template: CommandTemplate) => commandTemplatesApi.updateTemplate(template._id, {
      isQuickAction: !template.isQuickAction,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['command-templates'] });
      queryClient.invalidateQueries({ queryKey: ['command-templates-quick-actions'] });
    },
  });

  const templates = templatesData?.templates || [];

  const categories = [
    { id: 'all', name: 'All', icon: Terminal },
    { id: 'system', name: 'System', icon: Server },
    { id: 'docker', name: 'Docker', icon: Box },
    { id: 'network', name: 'Network', icon: Network },
    { id: 'monitoring', name: 'Monitoring', icon: Monitor },
    { id: 'maintenance', name: 'Maintenance', icon: RefreshCw },
    { id: 'custom', name: 'Custom', icon: Terminal },
  ];

  const getDangerBadge = (level: string) => {
    switch (level) {
      case 'safe': return 'badge-online';
      case 'warning': return 'badge-warning';
      case 'danger': return 'badge-error';
      default: return 'badge-offline';
    }
  };

  return (
    <div className="space-y-4">
      {/* Category tabs + create */}
      <div className="flex items-center gap-2 flex-wrap">
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setSelectedCategory(cat.id)}
            className={selectedCategory === cat.id ? 'tab-active' : 'tab'}
          >
            <cat.icon className="w-3.5 h-3.5" />
            {cat.name}
          </button>
        ))}
        <div className="flex-1" />
        <button onClick={() => setCreateModal(true)} className="btn-primary text-sm">
          <Plus className="w-4 h-4" /> Create
        </button>
      </div>

      {/* Templates list */}
      <div className="space-y-2">
        {isLoading ? (
          <div className="card p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-500" />
          </div>
        ) : templates.length === 0 ? (
          <div className="card p-8 text-center text-gray-500">
            No templates found
          </div>
        ) : (
          templates.map((template: CommandTemplate) => (
            <div key={template._id} className="card overflow-hidden">
              <div
                className="p-3 flex items-center gap-3 cursor-pointer hover:bg-dark-700/30 transition-colors"
                onClick={() => setExpandedTemplate(expandedTemplate === template._id ? null : template._id)}
              >
                {expandedTemplate === template._id ? (
                  <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-200 text-sm">{template.name}</span>
                    {template.isBuiltIn && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-dark-600 text-gray-500 rounded">Built-in</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 truncate">{template.description}</p>
                </div>
                <span className={`text-xs ${getDangerBadge(template.dangerLevel)}`}>
                  {template.dangerLevel}
                </span>
                <div className="flex gap-1.5" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => toggleQuickAction.mutate(template)}
                    className={`btn-ghost p-1.5 ${template.isQuickAction ? 'text-amber-400' : 'text-gray-600 hover:text-gray-400'}`}
                    title={template.isQuickAction ? 'Remove from Quick Actions' : 'Pin to Quick Actions'}
                  >
                    <Star className={`w-3.5 h-3.5 ${template.isQuickAction ? 'fill-amber-400' : ''}`} />
                  </button>
                  <button
                    onClick={() => setEditModal({ template })}
                    className="btn-ghost p-1.5 text-gray-400 hover:text-gray-200"
                    title="Edit"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => setExecuteModal({ template })}
                    disabled={!agentId}
                    className="btn-primary p-1.5"
                    title={agentId ? 'Execute' : 'Select a VPC first'}
                  >
                    <Play className="w-3.5 h-3.5" />
                  </button>
                  {!template.isBuiltIn && (
                    <button
                      onClick={() => { if (confirm('Delete this template?')) deleteMutation.mutate(template._id); }}
                      className="btn-ghost p-1.5 text-red-400"
                      title="Delete"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
              {expandedTemplate === template._id && (
                <div className="border-t border-dark-700 p-3 bg-dark-800/50 space-y-2">
                  <div>
                    <label className="text-xs text-gray-500">Command</label>
                    <code className="code text-xs block whitespace-pre-wrap mt-1">{template.command}</code>
                  </div>
                  {template.parameters.length > 0 && (
                    <div>
                      <label className="text-xs text-gray-500">Parameters</label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {template.parameters.map((p) => (
                          <span key={p.name} className="text-xs px-2 py-0.5 bg-dark-700 rounded text-gray-300">
                            <code className="text-neon-400">{`{{${p.name}}}`}</code> — {p.label}
                            {p.required && <span className="text-red-400 ml-1">*</span>}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex gap-4 text-[10px] text-gray-600 pt-1">
                    <span>Target: {template.targetType}</span>
                    <span>Used: {template.usageCount}x</span>
                    {template.lastUsed && <span>Last: {new Date(template.lastUsed).toLocaleDateString()}</span>}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Execute modal with streaming */}
      {executeModal && (
        <StreamingExecuteModal
          template={executeModal.template}
          agentId={agentId}
          agents={agents}
          onClose={() => {
            setExecuteModal(null);
            queryClient.invalidateQueries({ queryKey: ['command-templates'] });
          }}
        />
      )}

      {/* Edit modal */}
      {editModal && (
        <EditTemplateModal
          template={editModal.template}
          onClose={() => setEditModal(null)}
          onSuccess={() => {
            setEditModal(null);
            queryClient.invalidateQueries({ queryKey: ['command-templates'] });
            queryClient.invalidateQueries({ queryKey: ['command-templates-quick-actions'] });
          }}
        />
      )}

      {/* Create modal */}
      {createModal && (
        <CreateTemplateModal
          onClose={() => setCreateModal(false)}
          onSuccess={() => {
            setCreateModal(false);
            queryClient.invalidateQueries({ queryKey: ['command-templates'] });
            queryClient.invalidateQueries({ queryKey: ['command-templates-quick-actions'] });
          }}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────
// Live Console Panel
// ────────────────────────────────────────────

function LiveConsolePanel({ agentId, agents }: { agentId: string; agents: HostAgent[] }) {
  const [command, setCommand] = useState('');
  const [history, setHistory] = useState<string[]>([]);
  const [historyIdx, setHistoryIdx] = useState(-1);
  const [output, setOutput] = useState<OutputLine[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const outputRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [output]);

  const selectedAgent = agents.find(a => a._id === agentId);

  const runCommand = useCallback(() => {
    if (!command.trim() || !agentId || isRunning) return;

    setHistory(prev => [command, ...prev.slice(0, 49)]);
    setHistoryIdx(-1);
    setOutput(prev => [...prev, { type: 'command', text: `$ ${command}`, timestamp: new Date() }]);
    setIsRunning(true);

    const currentCommand = command;
    setCommand('');

    const controller = commandTemplatesApi.executeStream(agentId, currentCommand, {
      onStatus: (data) => {
        setOutput(prev => [...prev, { type: 'status', text: data.message, timestamp: new Date() }]);
      },
      onStdout: (data) => {
        setOutput(prev => [...prev, { type: 'stdout', text: data.line, timestamp: new Date() }]);
      },
      onStderr: (data) => {
        setOutput(prev => [...prev, { type: 'stderr', text: data.line, timestamp: new Date() }]);
      },
      onDone: (data) => {
        if (data.code !== 0) {
          setOutput(prev => [...prev, { type: 'error', text: `Exit code: ${data.code}`, timestamp: new Date() }]);
        }
        setIsRunning(false);
        setTimeout(() => inputRef.current?.focus(), 50);
      },
      onError: (data) => {
        setOutput(prev => [...prev, { type: 'error', text: data.message, timestamp: new Date() }]);
        setIsRunning(false);
      },
    });
    abortRef.current = controller;
  }, [command, agentId, isRunning]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      runCommand();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (historyIdx < history.length - 1) {
        const next = historyIdx + 1;
        setHistoryIdx(next);
        setCommand(history[next]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIdx > 0) {
        const next = historyIdx - 1;
        setHistoryIdx(next);
        setCommand(history[next]);
      } else if (historyIdx === 0) {
        setHistoryIdx(-1);
        setCommand('');
      }
    } else if (e.key === 'c' && e.ctrlKey) {
      abortRef.current?.abort();
      setIsRunning(false);
      setOutput(prev => [...prev, { type: 'error', text: '^C', timestamp: new Date() }]);
    }
  };

  return (
    <div className="flex flex-col">
      {/* Terminal */}
      <div className="flex items-center justify-between px-4 py-2 bg-dark-800 rounded-t-xl border border-dark-700 border-b-0">
        <div className="flex items-center gap-2 text-sm">
          <Terminal className="w-4 h-4 text-neon-400" />
          <span className="text-gray-300">
            {selectedAgent ? `${selectedAgent.name} (${selectedAgent.ipAddress})` : 'No VPC selected'}
          </span>
          {isRunning && <Loader2 className="w-3.5 h-3.5 animate-spin text-neon-400" />}
        </div>
        <button
          onClick={() => setOutput([])}
          className="btn-ghost p-1.5 text-gray-500 hover:text-gray-300"
          title="Clear"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div
        ref={outputRef}
        className="bg-dark-900 border-x border-dark-700 p-3 overflow-auto font-mono text-xs leading-relaxed min-h-[400px] max-h-[600px]"
        onClick={() => inputRef.current?.focus()}
      >
        {output.length === 0 && (
          <div className="text-gray-600 mb-4">
            {agentId
              ? 'Type a command below and press Enter. Use ↑/↓ for history, Ctrl+C to cancel.'
              : 'Select a VPC above to start.'
            }
          </div>
        )}
        {output.map((line, i) => (
          <div key={i} className={getLineClass(line.type)}>
            {line.text}
          </div>
        ))}
      </div>

      {/* Input */}
      <div className="flex items-center bg-dark-800 rounded-b-xl border border-dark-700 border-t-0 px-3 py-2 gap-2">
        <span className="text-neon-400 font-mono text-sm">$</span>
        <input
          ref={inputRef}
          type="text"
          className="flex-1 bg-transparent text-gray-200 font-mono text-sm outline-none placeholder-gray-600"
          value={command}
          onChange={(e) => setCommand(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={agentId ? 'Enter command...' : 'Select a VPC first'}
          disabled={!agentId}
          autoFocus
        />
        {isRunning ? (
          <button
            onClick={() => { abortRef.current?.abort(); setIsRunning(false); }}
            className="px-3 py-1 bg-red-600 text-white text-xs rounded-lg hover:bg-red-500"
          >
            <Square className="w-3 h-3" />
          </button>
        ) : (
          <button
            onClick={runCommand}
            disabled={!command.trim() || !agentId}
            className="px-3 py-1 bg-neon-600 text-white text-xs rounded-lg hover:bg-neon-500 disabled:opacity-40"
          >
            Run
          </button>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────
// Streaming Execute Modal
// ────────────────────────────────────────────

function StreamingExecuteModal({
  template,
  agentId,
  agents,
  onClose,
}: {
  template: CommandTemplate;
  agentId: string;
  agents: HostAgent[];
  onClose: () => void;
}) {
  const [localAgentId, setLocalAgentId] = useState(agentId);
  const [params, setParams] = useState<Record<string, any>>(() => {
    const defaults: Record<string, any> = {};
    template.parameters.forEach(p => {
      if (p.defaultValue) defaults[p.name] = p.defaultValue;
    });
    return defaults;
  });
  const [confirmed, setConfirmed] = useState(!template.requiresConfirmation);
  const [output, setOutput] = useState<OutputLine[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const outputRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [output]);

  const buildCommand = () => {
    return template.command.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) =>
      params[key] !== undefined ? String(params[key]) : `{{${key}}}`
    );
  };

  const execute = () => {
    if (!localAgentId || isRunning) return;
    const command = buildCommand();
    setIsRunning(true);
    setOutput([]);

    const controller = commandTemplatesApi.executeStream(localAgentId, command, {
      onStatus: (data) => {
        setOutput(prev => [...prev, { type: 'status', text: `⟳ ${data.message}`, timestamp: new Date() }]);
      },
      onCommand: (data: any) => {
        const prefix = data.total > 1 ? `[${data.index}/${data.total}] $ ` : '$ ';
        setOutput(prev => [...prev, { type: 'command', text: `${prefix}${data.command}`, timestamp: new Date() }]);
      },
      onStdout: (data) => {
        setOutput(prev => [...prev, { type: 'stdout', text: data.line, timestamp: new Date() }]);
      },
      onStderr: (data) => {
        setOutput(prev => [...prev, { type: 'stderr', text: data.line, timestamp: new Date() }]);
      },
      onDone: (data) => {
        setOutput(prev => [...prev, {
          type: data.code === 0 ? 'info' : 'error',
          text: `${data.code === 0 ? '✓' : '✗'} Done (exit code: ${data.code ?? 'N/A'})`,
          timestamp: new Date(),
        }]);
        setIsRunning(false);
        setIsDone(true);
      },
      onError: (data) => {
        setOutput(prev => [...prev, { type: 'error', text: `✗ ${data.message}`, timestamp: new Date() }]);
        setIsRunning(false);
        setIsDone(true);
      },
    });
    abortRef.current = controller;
  };

  const canExecute = localAgentId && confirmed && !isRunning && !isDone;

  return (
    <div className="modal-backdrop" onClick={() => !isRunning && onClose()}>
      <div className="modal-content max-w-3xl" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title flex items-center gap-2">
            <Play className="w-5 h-5 text-neon-400" />
            {template.name}
          </h3>
          <button onClick={onClose} disabled={isRunning} className="btn-ghost p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="modal-body space-y-4">
          {!agentId && (
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Target VPC</label>
              <select
                className="input w-full"
                value={localAgentId}
                onChange={(e) => setLocalAgentId(e.target.value)}
              >
                <option value="">Select...</option>
                {agents.map((a) => (
                  <option key={a._id} value={a._id}>{a.name} ({a.ipAddress})</option>
                ))}
              </select>
            </div>
          )}

          {template.parameters.length > 0 && (
            <div className="grid grid-cols-2 gap-3">
              {template.parameters.map((param) => (
                <div key={param.name}>
                  <label className="text-xs text-gray-500 mb-1 block">
                    {param.label} {param.required && <span className="text-red-400">*</span>}
                  </label>
                  {param.type === 'select' && param.options ? (
                    <select className="input w-full" value={params[param.name] || ''}
                      onChange={(e) => setParams({ ...params, [param.name]: e.target.value })}>
                      {param.options.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  ) : param.type === 'boolean' ? (
                    <input type="checkbox" checked={params[param.name] || false}
                      onChange={(e) => setParams({ ...params, [param.name]: e.target.checked })} />
                  ) : (
                    <input type={param.type === 'number' ? 'number' : 'text'} className="input w-full"
                      value={params[param.name] || ''}
                      onChange={(e) => setParams({ ...params, [param.name]: e.target.value })}
                      placeholder={param.description} />
                  )}
                </div>
              ))}
            </div>
          )}

          {template.requiresConfirmation && !isDone && (
            <div className={`p-3 rounded-lg border ${
              template.dangerLevel === 'danger'
                ? 'border-red-500/50 bg-red-500/10'
                : 'border-amber-500/50 bg-amber-500/10'
            }`}>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="w-4 h-4" />
                <span className={template.dangerLevel === 'danger' ? 'text-red-400 text-sm' : 'text-amber-400 text-sm'}>
                  I confirm this action
                </span>
              </label>
            </div>
          )}

          <div>
            <label className="text-xs text-gray-500 mb-1 block">Command{buildCommand().includes('\n') ? 's' : ''}</label>
            <div className="bg-dark-900 rounded-lg border border-dark-600 p-3 space-y-1">
              {buildCommand().split('\n').filter((l: string) => l.trim() && !l.trim().startsWith('#')).map((line: string, i: number, arr: string[]) => (
                <div key={i} className="flex items-center gap-2 font-mono text-xs">
                  {arr.length > 1 && (
                    <span className="flex-shrink-0 w-4 h-4 rounded-full bg-dark-700 text-gray-500 flex items-center justify-center text-[10px]">{i + 1}</span>
                  )}
                  <code className="text-neon-300">{line.trim()}</code>
                </div>
              ))}
            </div>
          </div>

          {output.length > 0 && (
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Output</label>
              <div ref={outputRef}
                className="bg-dark-900 rounded-lg p-3 max-h-72 overflow-auto font-mono text-xs leading-relaxed">
                {output.map((line, i) => (
                  <div key={i} className={getLineClass(line.type)}>{line.text}</div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} disabled={isRunning} className="btn-secondary">
            {isDone ? 'Close' : 'Cancel'}
          </button>
          {!isDone && (
            <button onClick={execute} disabled={!canExecute} className="btn-primary">
              {isRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              {isRunning ? 'Running...' : 'Execute'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────
// Create Template Modal
// ────────────────────────────────────────────

function CreateTemplateModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'custom',
    command: '',
    dangerLevel: 'safe',
    targetType: 'agent',
    requiresConfirmation: false,
    parameters: [] as Array<{ name: string; type: string; label: string; required: boolean; defaultValue: string }>,
  });
  const [newParam, setNewParam] = useState({ name: '', label: '', type: 'string', required: false, defaultValue: '' });

  const createMutation = useMutation({
    mutationFn: () => commandTemplatesApi.createTemplate(formData),
    onSuccess: () => onSuccess(),
  });

  const addParameter = () => {
    if (!newParam.name || !newParam.label) return;
    setFormData({
      ...formData,
      parameters: [...formData.parameters, { ...newParam }],
    });
    setNewParam({ name: '', label: '', type: 'string', required: false, defaultValue: '' });
  };

  const removeParameter = (idx: number) => {
    setFormData({
      ...formData,
      parameters: formData.parameters.filter((_, i) => i !== idx),
    });
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content max-w-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Create Command Template</h3>
          <button onClick={onClose} className="btn-ghost p-1"><X className="w-5 h-5" /></button>
        </div>

        <div className="modal-body space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Name</label>
              <input className="input w-full" value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Template name" />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Category</label>
              <select className="input w-full" value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}>
                <option value="system">System</option>
                <option value="docker">Docker</option>
                <option value="network">Network</option>
                <option value="monitoring">Monitoring</option>
                <option value="maintenance">Maintenance</option>
                <option value="custom">Custom</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-1 block">Description</label>
            <input className="input w-full" value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="What does this command do?" />
          </div>

          <CommandEditor
            value={formData.command}
            onChange={(v) => setFormData({ ...formData, command: v })}
          />

          {/* Parameters */}
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Parameters</label>
            {formData.parameters.length > 0 && (
              <div className="space-y-1 mb-3">
                {formData.parameters.map((p, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs bg-dark-700 p-2 rounded">
                    <code className="text-neon-400">{`{{${p.name}}}`}</code>
                    <span className="text-gray-400">{p.label}</span>
                    <span className="text-gray-600">({p.type})</span>
                    {p.required && <span className="text-red-400">*</span>}
                    <div className="flex-1" />
                    <button onClick={() => removeParameter(i)} className="text-red-400 hover:text-red-300">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-end gap-2">
              <input className="input flex-1" placeholder="name" value={newParam.name}
                onChange={(e) => setNewParam({ ...newParam, name: e.target.value })} />
              <input className="input flex-1" placeholder="Label" value={newParam.label}
                onChange={(e) => setNewParam({ ...newParam, label: e.target.value })} />
              <select className="input w-24" value={newParam.type}
                onChange={(e) => setNewParam({ ...newParam, type: e.target.value })}>
                <option value="string">String</option>
                <option value="number">Number</option>
                <option value="boolean">Boolean</option>
              </select>
              <button onClick={addParameter} disabled={!newParam.name || !newParam.label}
                className="btn-secondary p-2">
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Danger Level</label>
              <select className="input w-full" value={formData.dangerLevel}
                onChange={(e) => setFormData({ ...formData, dangerLevel: e.target.value })}>
                <option value="safe">Safe</option>
                <option value="warning">Warning</option>
                <option value="danger">Danger</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Target Type</label>
              <select className="input w-full" value={formData.targetType}
                onChange={(e) => setFormData({ ...formData, targetType: e.target.value })}>
                <option value="agent">VPC (Agent)</option>
                <option value="container">Container</option>
                <option value="server">Server</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={formData.requiresConfirmation}
                  onChange={(e) => setFormData({ ...formData, requiresConfirmation: e.target.checked })} />
                <span className="text-sm text-gray-400">Requires confirmation</span>
              </label>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button
            onClick={() => createMutation.mutate()}
            disabled={!formData.name || !formData.command || createMutation.isPending}
            className="btn-primary"
          >
            {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Create
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────
// Edit Template Modal
// ────────────────────────────────────────────

function EditTemplateModal({
  template,
  onClose,
  onSuccess,
}: {
  template: CommandTemplate;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [formData, setFormData] = useState({
    name: template.name,
    description: template.description,
    category: template.category,
    command: template.command,
    dangerLevel: template.dangerLevel,
    targetType: template.targetType,
    requiresConfirmation: template.requiresConfirmation,
    isQuickAction: template.isQuickAction || false,
    quickActionColor: template.quickActionColor || 'blue',
    quickActionIcon: template.quickActionIcon || 'terminal',
    parameters: template.parameters.map(p => ({
      name: p.name,
      type: p.type,
      label: p.label,
      required: p.required,
      defaultValue: p.defaultValue || '',
    })),
  });
  const [newParam, setNewParam] = useState({ name: '', label: '', type: 'string' as 'string' | 'number' | 'boolean' | 'select', required: false, defaultValue: '' });

  const updateMutation = useMutation({
    mutationFn: () => commandTemplatesApi.updateTemplate(template._id, formData),
    onSuccess: () => onSuccess(),
  });

  const addParameter = () => {
    if (!newParam.name || !newParam.label) return;
    setFormData({
      ...formData,
      parameters: [...formData.parameters, { ...newParam }],
    });
    setNewParam({ name: '', label: '', type: 'string', required: false, defaultValue: '' });
  };

  const removeParameter = (idx: number) => {
    setFormData({
      ...formData,
      parameters: formData.parameters.filter((_, i) => i !== idx),
    });
  };

  const colorOptions = ['blue', 'cyan', 'amber', 'purple', 'green', 'red', 'orange', 'neon'];
  const iconOptions = ['terminal', 'monitor', 'hard-drive', 'download', 'refresh-cw', 'cpu', 'network', 'zap', 'server', 'box', 'file-code', 'database'];

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title flex items-center gap-2">
            <Pencil className="w-5 h-5 text-neon-400" />
            Edit: {template.name}
          </h3>
          <button onClick={onClose} className="btn-ghost p-1"><X className="w-5 h-5" /></button>
        </div>

        <div className="modal-body space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Name</label>
              <input className="input w-full" value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                disabled={template.isBuiltIn} />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Category</label>
              <select className="input w-full" value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}>
                <option value="system">System</option>
                <option value="docker">Docker</option>
                <option value="network">Network</option>
                <option value="monitoring">Monitoring</option>
                <option value="maintenance">Maintenance</option>
                <option value="custom">Custom</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-1 block">Description</label>
            <input className="input w-full" value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
          </div>

          <CommandEditor
            value={formData.command}
            onChange={(v) => setFormData({ ...formData, command: v })}
          />

          {/* Parameters */}
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Parameters</label>
            {formData.parameters.length > 0 && (
              <div className="space-y-1 mb-3">
                {formData.parameters.map((p, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs bg-dark-700 p-2 rounded">
                    <code className="text-neon-400">{`{{${p.name}}}`}</code>
                    <span className="text-gray-400">{p.label}</span>
                    <span className="text-gray-600">({p.type})</span>
                    {p.required && <span className="text-red-400">*</span>}
                    <div className="flex-1" />
                    <button onClick={() => removeParameter(i)} className="text-red-400 hover:text-red-300">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-end gap-2">
              <input className="input flex-1" placeholder="name" value={newParam.name}
                onChange={(e) => setNewParam({ ...newParam, name: e.target.value })} />
              <input className="input flex-1" placeholder="Label" value={newParam.label}
                onChange={(e) => setNewParam({ ...newParam, label: e.target.value })} />
              <select className="input w-24" value={newParam.type}
                onChange={(e) => setNewParam({ ...newParam, type: e.target.value as any })}>
                <option value="string">String</option>
                <option value="number">Number</option>
                <option value="boolean">Boolean</option>
              </select>
              <button onClick={addParameter} disabled={!newParam.name || !newParam.label}
                className="btn-secondary p-2">
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Danger Level</label>
              <select className="input w-full" value={formData.dangerLevel}
                onChange={(e) => setFormData({ ...formData, dangerLevel: e.target.value as any })}>
                <option value="safe">Safe</option>
                <option value="warning">Warning</option>
                <option value="danger">Danger</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Target Type</label>
              <select className="input w-full" value={formData.targetType}
                onChange={(e) => setFormData({ ...formData, targetType: e.target.value as any })}>
                <option value="agent">VPC (Agent)</option>
                <option value="container">Container</option>
                <option value="server">Server</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={formData.requiresConfirmation}
                  onChange={(e) => setFormData({ ...formData, requiresConfirmation: e.target.checked })} />
                <span className="text-sm text-gray-400">Requires confirmation</span>
              </label>
            </div>
          </div>

          {/* Quick Action settings */}
          <div className="border border-dark-600 rounded-lg p-3 space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={formData.isQuickAction}
                onChange={(e) => setFormData({ ...formData, isQuickAction: e.target.checked })} />
              <Star className={`w-4 h-4 ${formData.isQuickAction ? 'text-amber-400 fill-amber-400' : 'text-gray-500'}`} />
              <span className="text-sm text-gray-300">Show as Quick Action</span>
            </label>
            {formData.isQuickAction && (
              <div className="grid grid-cols-2 gap-3 pt-1">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Color</label>
                  <div className="flex flex-wrap gap-1.5">
                    {colorOptions.map((c) => (
                      <button key={c}
                        onClick={() => setFormData({ ...formData, quickActionColor: c })}
                        className={`w-6 h-6 rounded-md bg-${c}-500/40 border-2 transition-all ${
                          formData.quickActionColor === c ? `border-${c}-400 scale-110` : 'border-transparent'
                        }`}
                        title={c}
                      />
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Icon</label>
                  <select className="input w-full text-sm" value={formData.quickActionIcon}
                    onChange={(e) => setFormData({ ...formData, quickActionIcon: e.target.value })}>
                    {iconOptions.map((ic) => (
                      <option key={ic} value={ic}>{ic}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button
            onClick={() => updateMutation.mutate()}
            disabled={!formData.name || !formData.command || updateMutation.isPending}
            className="btn-primary"
          >
            {updateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────
// Command Editor with line numbers & preview
// ────────────────────────────────────────────

function CommandEditor({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const lineNumbersRef = useRef<HTMLDivElement>(null);

  const lines = value.split('\n');
  const commandLines = lines.filter(l => l.trim() && !l.trim().startsWith('#'));

  const syncScroll = () => {
    if (textareaRef.current && lineNumbersRef.current) {
      lineNumbersRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm text-gray-400">Commands</label>
        <span className="text-[10px] text-gray-600">
          {commandLines.length} command{commandLines.length !== 1 ? 's' : ''} · runs sequentially
        </span>
      </div>

      {/* Editor with line numbers */}
      <div className="flex rounded-lg border border-dark-600 overflow-hidden bg-dark-900 focus-within:border-neon-500/50 transition-colors">
        {/* Line numbers */}
        <div
          ref={lineNumbersRef}
          className="flex-shrink-0 bg-dark-800 px-2 py-2.5 text-right select-none overflow-hidden border-r border-dark-700"
        >
          {lines.map((line, i) => {
            const trimmed = line.trim();
            const isComment = trimmed.startsWith('#');
            const isEmpty = !trimmed;
            return (
              <div key={i} className={`font-mono text-xs leading-[1.625rem] ${
                isComment ? 'text-gray-700' : isEmpty ? 'text-gray-700' : 'text-gray-500'
              }`}>
                {i + 1}
              </div>
            );
          })}
        </div>

        {/* Textarea */}
        <textarea
          ref={textareaRef}
          className="flex-1 bg-transparent text-gray-200 font-mono text-xs py-2.5 px-3 outline-none resize-none leading-[1.625rem] min-h-[130px] placeholder-gray-600"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onScroll={syncScroll}
          placeholder={`# One command per line — they run top to bottom\n# Lines starting with # are ignored\ncd /my/project\ngit pull origin main\npm2 restart my-app`}
          spellCheck={false}
        />
      </div>

      {/* Execution preview */}
      {commandLines.length > 1 && (
        <div className="bg-dark-800/50 border border-dark-700 rounded-lg p-3">
          <div className="flex items-center gap-1.5 text-[10px] text-gray-500 uppercase tracking-wider mb-2">
            <Info className="w-3 h-3" />
            Execution Plan
          </div>
          <div className="space-y-1">
            {commandLines.map((cmd, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className="flex-shrink-0 w-4 h-4 rounded-full bg-dark-700 text-gray-500 flex items-center justify-center text-[10px] font-mono">{i + 1}</span>
                <ArrowRight className="w-2.5 h-2.5 text-gray-600 flex-shrink-0" />
                <code className="text-neon-300 truncate">{cmd}</code>
                {i < commandLines.length - 1 && (
                  <span className="text-gray-600 text-[10px] flex-shrink-0">then →</span>
                )}
              </div>
            ))}
          </div>
          <p className="text-[10px] text-gray-600 mt-2">
            Stops on first error. <code className="text-gray-500">cd</code> carries over to next commands.
          </p>
        </div>
      )}

      {/* Inline tips */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-gray-600">
        <span>One command per line</span>
        <span>Lines with <code className="text-gray-500">#</code> are skipped</span>
        <span>Use <code className="text-gray-500">{'{{param}}'}</code> for dynamic values</span>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────

function getQuickActionIcon(icon?: string) {
  switch (icon) {
    case 'monitor': return Monitor;
    case 'hard-drive': return HardDrive;
    case 'download': return Download;
    case 'refresh-cw': return RefreshCw;
    case 'cpu': return Cpu;
    case 'network': return Network;
    case 'zap': return Zap;
    case 'server': return Server;
    case 'box': return Box;
    case 'file-code': return FileCode;
    default: return Terminal;
  }
}

function getLineClass(type: OutputLine['type']): string {
  switch (type) {
    case 'stdout': return 'text-gray-300 whitespace-pre-wrap';
    case 'stderr': return 'text-red-400 whitespace-pre-wrap';
    case 'command': return 'text-neon-400 font-bold mt-2 mb-1';
    case 'status': return 'text-gray-500 italic';
    case 'error': return 'text-red-400 font-medium';
    case 'info': return 'text-blue-400';
    default: return 'text-gray-400';
  }
}
